// export default CoursesCompletionTable;
import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Container,
  Header,
  Title,
  Controls,
  SearchInput,
  Select,
  ExportButton,
  TableWrapper,
  StyledTable,
  CourseHeaderCell,
  NameCell,
  SectorCell,
  StatusIcon,
  Pagination,
  PaginationButton,
  PageInfo,
  EmptyState,
  LoadingState
} from './style.js'; 

const CoursesCompletionTable = () => {
  const [employees, setEmployees] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortCourse, setSortCourse] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  // טעינת הנתונים
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // טעינת עובדים
        const empRes = await fetch('/api/users/my-employees', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const empData = await empRes.json();

        // טעינת קורסים
        const coursesRes = await fetch('/api/users/courses/available', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const coursesData = await coursesRes.json();

        if (empData.success) setEmployees(empData.employees || []);
        if (coursesData.success) setCourses(coursesData.courses || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // סינון עובדים לפי חיפוש
  let filteredEmployees = employees.filter(
    (emp) =>
      emp.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // סידור עובדים
  if (sortBy === 'name') {
    filteredEmployees.sort((a, b) =>
      (a.fullName || '').localeCompare(b.fullName || '', 'he')
    );
  } else if (sortBy === 'sector') {
    filteredEmployees.sort((a, b) =>
      (a.sector || '').localeCompare(b.sector || '', 'he')
    );
  } else if (sortCourse) {
    // Sorting by course completion
    filteredEmployees.sort((a, b) => {
      const aDone = isCourseDone(a, sortCourse) ? 1 : 0;
      const bDone = isCourseDone(b, sortCourse) ? 1 : 0;
      return sortOrder === 'asc' ? bDone - aDone : aDone - bDone;
    });
  }

  // חישוב pagination
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(
    startIdx,
    startIdx + itemsPerPage
  );

  // בדיקה אם קורס רלוונטי לעובד
  const isCourseMandatory = (employee, course) => {
    return (
      course.targetSectors && course.targetSectors.includes(employee.sector)
    );
  };

  // בדיקה אם העובד השלים קורס
  const isCourseDone = (employee, course) => {
    // בדוק ב-completedCourses או ב-courseProgress
    const inCompleted = employee.completedCourses?.some(
      (c) => c.courseSlug === course.slug && c.passed
    );

    const inProgress = employee.courseProgress?.some(
      (cp) => cp.courseSlug === course.slug && cp.passed
    );

    return inCompleted || inProgress;
  };

  // פונקציה ליצירת דוח אקסל
  const generateExcelReport = (filterType = 'all') => {
    // קביעת הנתונים לייצוא בהתאם לסנן
    let dataToExport = filteredEmployees;
    const coursesForReport = relevantCourses;

    if (filterType === 'completed') {
      // רק עובדים שסיימו את כל הקורסים שלהם
      dataToExport = filteredEmployees.filter((emp) => {
        const mandatoryCourses = coursesForReport.filter((course) =>
          isCourseMandatory(emp, course)
        );
        return (
          mandatoryCourses.length > 0 &&
          mandatoryCourses.every((course) => isCourseDone(emp, course))
        );
      });
    } else if (filterType === 'pending') {
      // עובדים שלא סיימו את כל הקורסים או טרם התחילו
      dataToExport = filteredEmployees.filter((emp) => {
        const mandatoryCourses = coursesForReport.filter((course) =>
          isCourseMandatory(emp, course)
        );
        return (
          mandatoryCourses.length === 0 ||
          !mandatoryCourses.every((course) => isCourseDone(emp, course))
        );
      });
    }

    // בנייה של הנתונים לטבלה
    const excelData = [
      [
        'שם עובד',
        'סקטור',
        'תאריך הצטרפות',
        'התחברות אחרונה',
        ...coursesForReport.map((c) => c.title),
        'סה"כ קורסים שלם',
        'סטטוס כללי',
      ],
      ...dataToExport.map((emp) => {
        const mandatoryCourses = coursesForReport.filter((course) =>
          isCourseMandatory(emp, course)
        );
        const completedCount = mandatoryCourses.filter((course) =>
          isCourseDone(emp, course)
        ).length;
        const allCompleted =
          mandatoryCourses.length > 0
            ? completedCount === mandatoryCourses.length
            : false;

        return [
          emp.fullName,
          emp.sector,
          new Date(emp.createdAt).toLocaleDateString('he-IL'),
          emp.lastLogin
            ? new Date(emp.lastLogin).toLocaleDateString('he-IL')
            : 'לא נכנס',
          ...coursesForReport.map((course) =>
            isCourseMandatory(emp, course)
              ? isCourseDone(emp, course)
                ? '✓'
                : '✗'
              : '-'
          ),
          mandatoryCourses.length > 0
            ? `${completedCount}/${mandatoryCourses.length}`
            : '0/0',
          allCompleted ? 'הושלם' : 'בתהליך',
        ];
      }),
    ];

    // יצירת ה-Workbook
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // עיצוב העמודות
    const colWidths = [
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      ...coursesForReport.map(() => ({ wch: 10 })),
      { wch: 12 },
      { wch: 10 },
    ];
    ws['!cols'] = colWidths;

    // עיצוב השורה הראשונה
    for (let i = 0; i < excelData[0].length; i++) {
      const cell = ws[XLSX.utils.encode_col(i) + '1'];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '667EEA' } },
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
          },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'דוח קורסים');

    // שם הקובץ בהתאם לסנן
    const dateStr = new Date().toLocaleDateString('he-IL');
    const filterName =
      filterType === 'all'
        ? 'כל_העובדים'
        : filterType === 'completed'
        ? 'השלימו'
        : 'בתהליך';
    const filename = `דוח_קורסים_${filterName}_${dateStr}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>טוען נתונים...</LoadingState>
      </Container>
    );
  }

  const relevantCourses = courses.filter((course) =>
    paginatedEmployees.some((emp) => isCourseMandatory(emp, course))
  );

  return (
    <Container>
      <Header>
        <Title>📊 טבלת התקדמות לומדות</Title>
      </Header>

      <Controls>
        <SearchInput
          type='text'
          placeholder='🔍 חיפוש לפי שם או מייל...'
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        <Select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(parseInt(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value={10}>10 עובדים</option>
          <option value={25}>25 עובדים</option>
          <option value={50}>50 עובדים</option>
          <option value={100}>100 עובדים</option>
        </Select>

        <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value='name'>סדר: שם (א-ב)</option>
          <option value='sector'>סדר: סקטור</option>
        </Select>

        <ExportButton variant='all' onClick={() => generateExcelReport('all')}>
          📊 ייצא הכל
        </ExportButton>
        <ExportButton
          variant='completed'
          onClick={() => generateExcelReport('completed')}
        >
          ✅ ייצא השלימו
        </ExportButton>
        <ExportButton
          variant='pending'
          onClick={() => generateExcelReport('pending')}
        >
          ⏳ ייצא בתהליך
        </ExportButton>
      </Controls>

      {filteredEmployees.length === 0 ? (
        <EmptyState>
          <p>לא נמצאו עובדים התואמים את החיפוש</p>
        </EmptyState>
      ) : (
        <>
          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <th>👤 שם עובד</th>
                  <th>📍 סקטור</th>
                  {relevantCourses.map((course) => (
                    <CourseHeaderCell
                      key={course.slug}
                      title={course.title}
                      onClick={() => {
                        if (sortCourse?.slug === course.slug) {
                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortCourse(course);
                          setSortOrder('asc');
                          setSortBy(null);
                        }
                      }}
                    >
                      {course.title}
                      {sortCourse?.slug === course.slug && (
                        <span style={{ marginRight: '4px' }}>
                          {sortOrder === 'asc' ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </CourseHeaderCell>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedEmployees.map((employee) => (
                  <tr key={employee._id}>
                    <NameCell>{employee.fullName}</NameCell>
                    <SectorCell>{employee.sector}</SectorCell>
                    {relevantCourses.map((course) => {
                      const isMandatory = isCourseMandatory(employee, course);
                      const isDone = isCourseDone(employee, course);

                      return (
                        <td key={`${employee._id}-${course.slug}`}>
                          {isMandatory ? (
                            <StatusIcon completed={isDone}>
                              {isDone ? '✅' : '❌'}
                            </StatusIcon>
                          ) : (
                            <span style={{ color: '#ccc' }}>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>

          <Pagination>
            <PaginationButton
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              « התחלה
            </PaginationButton>

            <PaginationButton
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ‹ קודם
            </PaginationButton>

            {Array.from({ length: totalPages }, (_, i) => {
              const page = i + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <PaginationButton
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </PaginationButton>
                );
              }
              return null;
            })}

            <PaginationButton
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              הבא ›
            </PaginationButton>

            <PaginationButton
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              סוף »
            </PaginationButton>

            <PageInfo>
              דף {currentPage} מתוך {totalPages} ({filteredEmployees.length}{' '}
              עובדים)
            </PageInfo>
          </Pagination>
        </>
      )}
    </Container>
  );
};

export default CoursesCompletionTable;